import { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signInWithRedirect } from 'aws-amplify/auth';
import { useAuth } from '../contexts/AuthContext';
import { PasswordInput } from '../components/auth/PasswordInput';
import { useSeo } from '../hooks/useSeo';

export function Signup() {
  useSeo('Create Account — Media Magic');
  const [step, setStep] = useState<'signup' | 'confirm'>('signup');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [localError, setLocalError] = useState('');
  const { signup, confirmSignup, loading, error, clearError } = useAuth();
  const navigate = useNavigate();

  async function handleSignup(e: FormEvent) {
    e.preventDefault();
    setLocalError('');
    clearError();

    if (!username || !email || !password || !confirmPassword) {
      setLocalError('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setLocalError('Password must be at least 8 characters');
      return;
    }

    try {
      await signup(username, email, password);
      setStep('confirm');
    } catch {
      // Error is set in context
    }
  }

  async function handleConfirm(e: FormEvent) {
    e.preventDefault();
    setLocalError('');
    clearError();

    if (!code) {
      setLocalError('Please enter the confirmation code');
      return;
    }

    try {
      await confirmSignup(username, code);
      navigate('/login');
    } catch {
      // Error is set in context
    }
  }

  const displayError = localError || error;

  return (
    <div className="auth-screen">
      <div className="auth-layout">
        <aside className="auth-story">
          <span className="story-kicker">Make every image count</span>
          <h1>Turn visuals into a growth advantage.</h1>
          <p>
            Get started in minutes and give your users faster loads, cleaner crops, and consistently better visual quality.
          </p>
          <ul className="benefit-list" aria-label="Signup benefits">
            <li>Built-in optimization without heavy setup</li>
            <li>Smarter content framing for every format</li>
            <li>Analytics that connect performance to outcomes</li>
          </ul>
        </aside>

        <section className="auth-card-wrap">
          <div className="auth-card">
            <h2 className="auth-title">{step === 'confirm' ? 'Confirm your account' : 'Create your account'}</h2>
            <p className="auth-subtitle">
              {step === 'confirm'
                ? `Enter the verification code sent to ${email}.`
                : 'Start free and see impact from day one.'}
            </p>

            {displayError && <div className="auth-error">{displayError}</div>}

            {step === 'signup' && (
              <>
                <button
                  type="button"
                  className="btn btn-google auth-submit"
                  onClick={() => signInWithRedirect({ provider: 'Google' })}
                  disabled={loading}
                >
                  <svg className="google-icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  Sign up with Google
                </button>

                <div className="auth-divider">
                  <span>or continue with email</span>
                </div>
              </>
            )}

            {step === 'confirm' ? (
              <form onSubmit={handleConfirm} className="auth-form">
                <label className="auth-label" htmlFor="code">
                  Confirmation code
                </label>
                <input
                  id="code"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="auth-input"
                  placeholder="Enter 6-digit code"
                  disabled={loading}
                />

                <button type="submit" className="btn btn-solid auth-submit" disabled={loading}>
                  {loading ? 'Confirming...' : 'Confirm Account'}
                </button>
                <button
                  type="button"
                  onClick={() => setStep('signup')}
                  className="btn btn-ghost auth-back"
                  disabled={loading}
                >
                  Back to signup
                </button>
              </form>
            ) : (
              <form onSubmit={handleSignup} className="auth-form">
                <label className="auth-label" htmlFor="signup-username">
                  Username
                </label>
                <input
                  id="signup-username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="auth-input"
                  placeholder="Choose a username"
                  disabled={loading}
                />

                <label className="auth-label" htmlFor="signup-email">
                  Email
                </label>
                <input
                  id="signup-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="auth-input"
                  placeholder="Enter your email"
                  disabled={loading}
                />

                <label className="auth-label" htmlFor="signup-password">
                  Password
                </label>
                <PasswordInput
                  id="signup-password"
                  value={password}
                  onChange={setPassword}
                  placeholder="At least 8 characters"
                  disabled={loading}
                  autoComplete="new-password"
                />

                <label className="auth-label" htmlFor="signup-confirm-password">
                  Confirm password
                </label>
                <PasswordInput
                  id="signup-confirm-password"
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  placeholder="Repeat your password"
                  disabled={loading}
                  autoComplete="new-password"
                />

                <button type="submit" className="btn btn-solid auth-submit" disabled={loading}>
                  {loading ? 'Creating account...' : 'Create Account'}
                </button>
              </form>
            )}

            <p className="auth-footer">
              Already have an account?{' '}
              <Link to="/login" className="auth-link">
                Sign in
              </Link>
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}

export default Signup;
